import React from 'react';
import { useTranslation } from 'react-i18next';

import { LineChart, Skeleton, DayBars, type ChartSeries, type DayBarSeries } from '../../../../components';
import { HOME_SERIES_COLORS } from '../../../../theme/tokens';
import type { HomePeriod, StatsNewPlayers } from '../../../../mock-data/types';
import { StatCard } from '../StatCard';
import { PrimaryStat } from '../PrimaryStat';
import { Legend } from '../Legend';
import { Inner, StatsRow, Cell, VDivider, ChartCol, LegendWrap } from './NewPlayers.styles';

/** Compact count axis ("80", "1.2k"). */
const countFmt = (n: number): string => (n >= 1000 ? `${Math.round(n / 100) / 10}k` : `${Math.round(n)}`);

// Mirrors the LineChart `height` so the placeholder reserves the same band.
const CHART_HEIGHT = 170;

export type NewPlayersProps = {
  data: StatsNewPlayers;
  /** The selected range — drives whether the trend chart or the day-comparison bars
   *  show (Today/Yesterday have no usable hourly slices). */
  interval: HomePeriod;
  /** While true, the figures + trend chart show placeholders (the endpoint is loading). */
  loading?: boolean;
};

/**
 * "New Players Joining Your Casino" — the Registration + FTDs lead figures beside
 * a two-series trend chart. The time range is driven globally from the page header;
 * on the Today / Yesterday ranges the trend collapses to a prior-day vs current-day
 * {@link DayBars} comparison (no usable hourly slices there).
 */
export const NewPlayers = ({ data, interval, loading = false }: NewPlayersProps): React.ReactElement => {
  const { t } = useTranslation();
  const dayView = interval === 'TODAY' || interval === 'YESTERDAY';
  const series: ChartSeries[] = [
    { name: t('Registrations'), color: HOME_SERIES_COLORS.registrations, points: data.chart.registrations },
    { name: t('FTDs'), color: HOME_SERIES_COLORS.ftds, points: data.chart.ftds },
  ];
  const dayBars: DayBarSeries[] = [
    { name: t('Registrations'), color: HOME_SERIES_COLORS.registrations, ...data.days.registrations },
    { name: t('FTDs'), color: HOME_SERIES_COLORS.ftds, ...data.days.ftds },
  ];
  return (
    <StatCard
      title={t('New Players Joining Your Casino')}
      sub={t('How many people are coming in and are they converting?')}
    >
      <Inner>
        <StatsRow>
          <Cell>
            <PrimaryStat data={data.registration} loading={loading} />
          </Cell>
          <VDivider />
          <Cell>
            <PrimaryStat data={data.ftds} loading={loading} />
          </Cell>
        </StatsRow>
        <ChartCol>
          {loading ? (
            <Skeleton block height={CHART_HEIGHT} radius={12} />
          ) : dayView ? (
            <DayBars series={dayBars} view={interval === 'TODAY' ? 'today' : 'yesterday'} height={CHART_HEIGHT} formatValue={countFmt} />
          ) : (
            <LineChart series={series} xLabels={data.chart.labels} pointLabels={data.chart.pointLabels} height={CHART_HEIGHT} yTicks={3} formatY={countFmt} />
          )}
          <LegendWrap>
            <Legend
              items={[
                { name: t('Registrations'), color: HOME_SERIES_COLORS.registrations },
                { name: t('FTDs'), color: HOME_SERIES_COLORS.ftds },
              ]}
            />
          </LegendWrap>
        </ChartCol>
      </Inner>
    </StatCard>
  );
};

export default NewPlayers;
